import { animate, AnimationEvent, style, transition, trigger, useAnimation } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, Directive, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, TemplateRef } from '@angular/core';
import { logUserAction } from '@office-start/telemetry-service';
import { Area } from '@office-start/telemetry-types';

import { ApplicationError } from '../../../error-handler/error-handler.service';
import { fadeInAndScaleDown } from '../../animations/animations';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../instrumentation/instrumentation.service';
import { ItemView } from '../../models/item-view.model';
import { ListItem } from '../../models/list-item.model';
import { BrowserType, DeviceDetector } from '../../services/device-detector.service';
import { CapabilitiesService } from '../../shared-context/capabilities.service';
import { ContextService } from '../../shared-context/context.service';
import { LocService } from '../../shared-context/loc.service';
import { ExpandableListLoadAnimation, ExpandableListModel } from './expandable-list.model';
import { ListState } from './list-state.enum';

// This class is a directive used in conjunction with the ExpandableList Component
// Any content that is placed as a child of a node with this directive will be repeated in the ExpandableList as a List Item
// Usage: *ohpListItem="let item = item; let index = index"
// Don't forget the '*' otherwise, it will not work properly!
@Directive({
    selector: '[ohpListItem]'
})
export class ExpandableListItemDirective {
    constructor(
        public templateRef: TemplateRef<any>
    ) { }
}

@Component({
    selector: 'ohp-expandable-list',
    templateUrl: './expandable-list.component.html',
    animations: [
        trigger('fadeInOut', [
            transition('viewRendered => animateTabEntrance', [
                style({ opacity: 0, transform: 'translateX(30px)' }),
                animate('250ms 67ms cubic-bezier(0.26,1.00,0.48,1.00)', style({ opacity: 1, transform: 'translateX(0)' }))
            ]),
            transition('viewRendered => animateTabEntranceRTL', [
                style({ opacity: 0, transform: 'translateX(-30px)' }),
                animate('250ms 67ms cubic-bezier(0.26,1.00,0.48,1.00)', style({ opacity: 1, transform: 'translateX(0)' }))
            ]),
            /* on initial render grow vertically or fadeAndScale with page instead of slide in */
            transition('void => viewRenderedSlide', [
                style({ opacity: 0, height: 0 }),
                animate('200ms 10ms ease-out', style({ opacity: 1, height: '*' }))
            ]),
            transition('void => viewRenderedFadeAndScale', useAnimation(fadeInAndScaleDown, { params: { delay: '98ms' }}))
        ])
    ]
})
export class ExpandableListComponent implements OnInit, AfterViewInit, OnChanges {
    @ContentChildren(ExpandableListItemDirective) public expandableListItems: QueryList<ExpandableListItemDirective>;
    public listItemTemplate: TemplateRef<any>;

    @Input() public listId: string;

    @Input() public area: string;

    @Input() public listModel: ExpandableListModel; // TODO: convert this and all usages to `Readonly<ExpandableListModel>` to support `ChangeDetectionStrategy.OnPush` for this component

    @Input() public itemView: ItemView;

    @Input() public set listState(listState: ListState) {
        // Since we modify 'listState' from within this component, we can't use `ngOnChanges` here.
        this.isListOpen = listState === ListState.Open;
        this._listState = listState;
    }

    public get listState(): ListState {
        return this._listState;
    }

    @Input() public selectedTab: number;

    @Output() public readonly listStateChange: EventEmitter<ListState> = new EventEmitter<ListState>();

    public animationState: string;
    public collapsing: boolean;
    public isTileView: boolean;
    public isCardView: boolean;
    public isListColView: boolean;
    public isListOpen: boolean;
    public isEvenPadding: boolean;
    public isTileOrCardView: boolean;
    public isVisualRefresh: boolean;
    public mobileMenusEnabled: boolean;
    public expandedFromKeyboard: boolean;
    public noOfPreviousItemsShown: number;
    public seeMoreLinkId: string;
    public showMoreButtonTextDefault: string;
    public showMoreButtonAriaLabelDefault: string;
    public showLessButtonTextDefault: string;
    public showLessButtonAriaLabelDefault: string;
    public updateAnnouncement: string;
    public isMobile: boolean;
    public isSmallMobileCards: boolean;
    public fluentRefresh: boolean;
    private _listState: ListState;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly locService: LocService,
        private readonly instrumentationService: InstrumentationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly deviceDetector: DeviceDetector
    ) {
    }

    public ngAfterViewInit() {
        if (this.expandableListItems.length !== 1) {
            throw new ApplicationError('Must have exactly 1 *ohpListItem in an ExpandableList as a child');
        }
        if (!this.listId) {
            throw new ApplicationError('Must include input "listId"');
        }
        if (!this.area) {
            throw new ApplicationError('Must include input "area"');
        }
        this.listItemTemplate = this.expandableListItems.first.templateRef;
        this.changeDetectorRef.detectChanges();
    }

    public ngOnInit() {
        if (this.listModel) {
            this.isSmallMobileCards = this.listModel.options?.isSmallMobileCards;
            this.isEvenPadding = this.listModel.options?.isEvenPadding;
            this.isVisualRefresh = this.listModel.options?.isVisualRefresh;
        }

        this.showMoreButtonTextDefault = this.locService.ShowMoreResults;
        this.showLessButtonTextDefault = this.locService.ShowLessResults;
        this.showMoreButtonAriaLabelDefault = this.locService.ShowMoreResults;
        this.showLessButtonAriaLabelDefault = this.locService.ShowLessResults;

        // Due to a bug in Angular 6 animations, the FadeAndScale animation does not work in Safari. Once that's fixed we can switch back to using FadeAndScale in all browsers.
        // https://github.com/angular/angular/issues/24490
        const animation = !this.listModel || (this.deviceDetector.deviceInfo.browser.browserType === BrowserType.Safari && this.listModel.animationOnLoad === ExpandableListLoadAnimation.FadeAndScale)
            ? ExpandableListLoadAnimation.None
            : this.listModel.animationOnLoad;
        this.animationState = 'viewRendered' + animation;
        this.fluentRefresh = this.capabilitiesService.capabilities.fluentRefresh;
        this.mobileMenusEnabled = this.capabilitiesService.capabilities.mobileContextMenusEnabled;
        this.isMobile = this.deviceDetector.isMobileDevice();
    }

    public ngOnChanges(changesObj: SimpleChanges) {
        if (changesObj.hasOwnProperty('selectedTab')) {
            if (this.contextService.isRtl) {
                this.animationState = 'animateTabEntranceRTL';
            } else {
                this.animationState = 'animateTabEntrance';
            }
        }
        if (changesObj.hasOwnProperty('itemView')) {
            this.isCardView = this.itemView === ItemView.Card;
            this.isTileView = this.itemView === ItemView.Tile;
            this.isTileOrCardView = this.isCardView || this.isTileView;
            this.isListColView = this.itemView === ItemView.ListCol;
        }
        if (changesObj.hasOwnProperty('listId')) {
            this.seeMoreLinkId = `see-more-items-${this.listId}`;
        }
    }

    public toggleShowMore(fromKeyboard?: boolean) {
        const collapsing = this.isListOpen;
        this.collapsing = collapsing;
        this.updateAnnouncement = collapsing ? this.listModel.showLessUpdateAriaLabel : this.listModel.showMoreUpdateAriaLabel;
        if (this.listModel.options?.infiniteShowMoreEnabled) {
            // Get no. of items in the list
            this.noOfPreviousItemsShown = this.listModel.itemsShown(fromKeyboard, this.isTileOrCardView);
            this.listModel.infiniteShowMore();
            this.expandedFromKeyboard = fromKeyboard;
        }
        // Delay animation to allow time for animation state change
        setTimeout(() => this.animate(collapsing, fromKeyboard), 0);
    }

    public toggleShowMoreKeyboard(event: KeyboardEvent) {
        if (event.keyCode !== 13) {
            return;
        }
        event.preventDefault(); // Prevent triggering the (click) handler
        this.toggleShowMore(true);
    }

    public animate(closing: boolean, fromKeyboard: boolean) {
        this.listState = closing ? ListState.Closed : ListState.Open;
        const displayedItemCount = this.getDisplayedItemCount();
        this.listStateChange.emit(this.listState);
        if (fromKeyboard && this.listState === ListState.Open) {
            this.expandedFromKeyboard = true;
        }

        if (this.listState === ListState.Closed) {
            this.expandedFromKeyboard = false;
        }

        this.listModel.onImpression(displayedItemCount, this.itemView);
        this.instrumentToggle();
    }

    public onAnimationCompleted(event: AnimationEvent) {
        // reset to base state at end of the animation. Avoid doing this if animation has been interrupted.
        if (event.toState === this.animationState) {
            this.animationState = 'viewRendered';
        }
    }

    public trackById(_index: number, item: ListItem) {
        return item.getId();
    }

    public getDisplayedItemCount(): number {
        return this.listModel.itemsShown(this.isListOpen, this.isTileOrCardView);
    }

    public currentState() {
        return this.animationState;
    }

    public shouldFocusOnLoad(index: number): boolean {
        if (this.listModel.options?.infiniteShowMoreEnabled) {
            return this.expandedFromKeyboard && this.noOfPreviousItemsShown === index;
        }

        const initialNumItems = this.isTileOrCardView ? this.listModel.initialListItemCount : this.listModel.options.initialTileItemCount;
        return this.expandedFromKeyboard && initialNumItems === index;
    }

    private instrumentToggle() {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Action] = ListState[this.listState];
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.ExpandableListToggle, this.seeMoreLinkId, this.area, null, null, params);
        logUserAction({
            eventName: KnownInstrumentationEvents.ExpandableListToggle,
            id: this.seeMoreLinkId,
            area: this.area as Area,
            result: this.listState === ListState.Closed ? 'ShowLess' : 'ShowMore',
            target: 'ExpandableList'
        });
    }
}
